import { CreditCardIcon } from '@heroicons/react/outline';
import type { CreateCardParams } from 'api/v2/connectors/createCard';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FreeLoader } from 'components/atoms/Loader/Loader.component';
import useCreateCard from 'hooks/Bank/useCreateCard';
import useGetCardsProductsList from 'hooks/Bank/useGetCardsProductsList';
import { createActivationResumptionURL } from 'hooks/Bank/useActivationFlowResumption';
import type { FC, ImgHTMLAttributes } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import { PostAccountStepId } from '../../constants/stepIds';
import type { ActivationStepProps } from '../../types';

const OptimizedCardImage: FC<ImgHTMLAttributes<HTMLImageElement>> = ({
  className,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <div
        className={classNames(
          'flex items-center justify-center bg-gray-100 text-gray-400',
          className,
        )}
      >
        <CreditCardIcon className="w-4 h-4" />
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div
          className={classNames(
            'flex items-center justify-center bg-gray-100 animate-pulse',
            className,
          )}
        >
          <div className="bg-gray-200 rounded w-full h-full" />
        </div>
      )}
      <img
        {...props}
        onLoad={handleLoad}
        onError={handleError}
        className={classNames(
          'transition-opacity duration-200',
          isLoading ? 'opacity-0' : 'opacity-100',
          className,
        )}
        style={{
          aspectRatio: '5/3',
        }}
      />
    </>
  );
};

const CardSelectionSkeleton: FC = () => (
  <div className="flex justify-center gap-4">
    {[1, 2].map((i) => (
      <div
        key={i}
        className="relative overflow-hidden rounded-lg border-2 border-gray-200 flex-shrink-0"
      >
        <div className="p-3">
          <div className="aspect-[5/3] w-15 h-9 bg-gray-200 rounded-sm animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Card design selection step within the IssueCard flow
 * Allows user to choose card design before creating
 */
const CardDesignStep: FC<ActivationStepProps> = ({
  onContinue: _,
  onBack,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
}) => {
  const { t } = useTranslation();
  const { data: cardsProductsList, isLoading: isLoadingCards } =
    useGetCardsProductsList();
  const createCardMutation = useCreateCard();
  const [selectedDesign, setSelectedDesign] =
    useState<CreateCardParams['design']>('aldo');

  const selectedCardProduct = cardsProductsList?.find(
    (product) => product.label === selectedDesign,
  );

  const handleCreateCard = () => {
    const successUri = createActivationResumptionURL(
      PostAccountStepId.CardDesign,
    );
    const errorUri = createActivationResumptionURL(
      PostAccountStepId.CardDesign,
      {
        cardCreationError: 'true',
      },
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('[CardDesignStep] Creating card with redirect URIs:', {
        successUri,
        errorUri,
        design: selectedDesign,
      });
    }

    createCardMutation.mutate({
      type: 'virtual',
      design: selectedDesign,
      successRedirectUri: successUri,
      errorRedirectUri: errorUri,
    });
    // After Swan completion, user returns via ?resumeActivation=card_design
  };

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      <div className="space-y-6">
        {isLoadingCards ? (
          <>
            {/* Large card skeleton */}
            <div className="flex justify-center">
              <div className="relative max-w-sm w-full">
                <div className="aspect-[5/3] bg-gray-200 rounded-xl shadow-lg animate-pulse" />
              </div>
            </div>

            {/* Selection buttons skeleton */}
            <CardSelectionSkeleton />
          </>
        ) : (
          <>
            {/* Selected Design Preview */}
            {selectedCardProduct && (
              <div className="space-y-2">
                {/* Big Card Preview */}
                <div className="flex justify-center">
                  <div className="relative max-w-sm w-full">
                    <div className="aspect-[5/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-lg overflow-hidden relative">
                      <OptimizedCardImage
                        loading="eager"
                        src={selectedCardProduct.cardDesignUrl}
                        alt={selectedCardProduct.label}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
                {/* Design Name */}
                <div className="text-center">
                  <span className="inline-flex items-center text-sm font-medium text-black capitalize">
                    {selectedCardProduct.label}
                  </span>
                </div>
              </div>
            )}

            {/* Card Design Selection */}
            {!isLoadingCards &&
            cardsProductsList &&
            cardsProductsList?.length > 0 ? (
              <div className="space-y-4">
                <div className="flex justify-center gap-4">
                  {cardsProductsList.map((product) => (
                    <button
                      key={product.id}
                      onClick={() =>
                        setSelectedDesign(
                          product.label as CreateCardParams['design'],
                        )
                      }
                      className={classNames(
                        'relative overflow-hidden rounded-lg border-2 transition-all duration-200 hover:shadow-md flex-shrink-0',
                        selectedDesign === product.label
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300',
                      )}
                    >
                      <div className="relative w-15 h-9 m-3">
                        <OptimizedCardImage
                          decoding="async"
                          loading="lazy"
                          src={product.cardDesignUrl}
                          alt={product.label}
                          className="h-full object-cover rounded-sm"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <CardSelectionSkeleton />
            )}
          </>
        )}

        {/* Action Button */}
        <Button
          onClick={handleCreateCard}
          disabled={createCardMutation.isLoading || !selectedCardProduct}
          className="w-full"
          color="bank"
        >
          {createCardMutation.isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <FreeLoader className="w-4 h-4 text-white" />
              <span>{t('loading')}</span>
            </div>
          ) : (
            t('continue')
          )}
        </Button>
      </div>
    </OnboardingStepWrapper>
  );
};

export default CardDesignStep;
